import React from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';

import { useRemoveCommunityUsersMutation } from 'generated/graphql';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { BackButton } from 'retired/BackButtonLayout/BackButton';
import { primaryBlue, primaryRed } from 'utils/color-styles';
import { MembersList } from 'containers/CommunityPage/RemoveMembersModal/MembersList';

import type { CommunityMember } from '../types';

interface Props {
  members: CommunityMember[];
  communityId: string;
  onClickClose: () => void;
  removeMember: (member: CommunityMember) => void;
  isExcludingMembers: boolean;
  selectedCount: number;
  totalMembers: number;
}

export const RemoveMembersModal = ({
  members,
  communityId,
  onClickClose,
  removeMember,
  isExcludingMembers,
  selectedCount,
  totalMembers,
}: Props) => {
  const [removeCommunityUsers, { loading, error }] = useRemoveCommunityUsersMutation({
    onCompleted: () => {
      onClickClose();
    },
    variables: {
      communityId,
      userIds: members.map((m) => m.User.id),
      isExcludingMembers,
    },
    // Evict the removed members from the cache
    update(cache) {
      const normalizedId = cache.identify({ id: communityId, __typename: 'Community' });
      cache.evict({
        id: normalizedId,
        fieldName: 'membersCount',
      });
      cache.evict({
        id: normalizedId,
        fieldName: 'members',
      });
      cache.gc();
    },
  });

  return (
    <Modal open fullWidth onClose={onClickClose}>
      <ModalContents
        onClickClose={onClickClose}
        body={
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BackButton onClick={onClickClose} />
              <ModalTitle style={{ marginBottom: 0, marginLeft: 12 }}>
                Remove {selectedCount} member{selectedCount > 1 ? 's' : ''}
              </ModalTitle>
            </div>
            <Box paddingTop={4} paddingBottom={4}>
              {error ? (
                <Alert severity="error" sx={{ '& .v5-MuiAlert-message': { marginTop: '2px' } }}>
                  An error occurred while removing users from the community. Please try again later.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ '& .v5-MuiAlert-message': { marginTop: '2px' } }}>
                  Confirm to remove <strong>{selectedCount}</strong> member
                  {selectedCount > 1 ? 's' : ''}? They will NOT be notified.
                </Alert>
              )}
            </Box>

            <MembersList
              communityId={communityId}
              members={members}
              isExcludingMembers={isExcludingMembers}
              removeMember={removeMember}
              totalMembers={totalMembers}
            />
          </div>
        }
        actions={
          <>
            <ModalButton
              style={{ color: primaryBlue }}
              variant="outlined"
              onClick={onClickClose}
              disabled={loading}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={selectedCount === 0 || loading}
              style={{ backgroundColor: primaryRed }}
              onClick={() => {
                void removeCommunityUsers();
              }}
            >
              {loading ? <CircularProgress size={21} style={{ color: 'white' }} /> : 'Remove'}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};
